import * as React from "react";
import { cn } from "@/lib/utils";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./hover-card";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";

export interface TimelineItem {
  id: string;
  description: string;
  descriptionContent?: React.ReactNode;
  hoverContent?: React.ReactNode;
  /** Where in the timeline the viewer is; its circle is filled. */
  current?: boolean;
}

export interface TimelineProps extends React.ComponentProps<typeof HoverCardPrimitive.Content> {
  items: TimelineItem[];
  className?: string;
  defaultCollapsed?: boolean;
  collapsedCount?: number;
  enableHoverCard?: boolean;
}

const Timeline = React.forwardRef<HTMLDivElement, TimelineProps>(
  ({ items, className, defaultCollapsed = false, enableHoverCard = false, ...props }, ref) => {
    // Expands whenever collapsing would hide the current item: at first, and each time it moves.
    const hidesCurrent = items.slice(1, -1).some((item) => item.current);
    const currentId = items.find((item) => item.current)?.id;
    const [isCollapsed, setIsCollapsed] = React.useState(() => defaultCollapsed && !hidesCurrent);
    const [seenCurrentId, setSeenCurrentId] = React.useState(currentId);
    if (currentId !== seenCurrentId) {
      setSeenCurrentId(currentId);
      if (hidesCurrent) setIsCollapsed(false);
    }

    // Always show first and last items when collapsed
    // Middle items are the ones that get hidden
    const getVisibleItems = () => {
      if (!isCollapsed || items.length <= 2) {
        return items;
      }

      // Show first item, then middle items are hidden, then last item
      // Safe: items.length > 2 is guaranteed by the guard above
      return [items[0], items.at(-1)!];
    };

    const visibleItems = getVisibleItems();
    const hiddenCount = items.length - 2; // Total items minus first and last

    return (
      <div ref={ref} className={cn("flex flex-col", className)}>
        {visibleItems.map((item, index) => {
          const isFirstItem = index === 0;
          const isLastItem = index === visibleItems.length - 1;
          const actualIndex = isCollapsed && !isFirstItem ? items.length - 1 : index;

          return (
            <React.Fragment key={item.id}>
              <TimelineItemComponent
                item={item}
                isLast={actualIndex === items.length - 1}
                showLine={!isLastItem || !isCollapsed}
                enableHoverCard={enableHoverCard}
                hoverContent={item.hoverContent}
                {...props}
              />

              {/* Show expand button after first item when collapsed */}
              {isCollapsed && isFirstItem && items.length > 2 && (
                <div className="flex gap-2 group relative mt-[-7px] before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[1px] before:bg-gray-400 before:ml-[5px] before:mt-1 z-20">
                  <div className="flex flex-col items-center flex-shrink-0 mt-[7px]">
                    {/* Open circle */}
                    <div className="w-3 h-3 rounded-full border border-gray-400 bg-white flex-shrink-0 z-10" />
                    {/* Vertical line */}
                    <div className="w-0.5 flex-grow bg-[var(--other--divider-2,#e7ebf1)] min-h-[8px]" />
                  </div>
                  <div className="flex-1 mb-[14px] mt-[3px] min-w-0">
                    <button
                      onClick={() => setIsCollapsed(!isCollapsed)}
                      className="text-sm text-[var(--text--tertiary,rgba(42,42,42,0.56))] hover:text-[var(--text--secondary,rgba(42,42,42,0.66))] underline decoration-solid text-left transition-colors rounded-md px-2 py-1 my-[-4px] ml-[-4px] block w-full break-words whitespace-normal"
                    >
                      + {hiddenCount} more
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
);
Timeline.displayName = "Timeline";

interface TimelineItemComponentProps extends React.ComponentProps<typeof HoverCardPrimitive.Content> {
  item: TimelineItem;
  isLast: boolean;
  showLine: boolean;
  hoverContent?: React.ReactNode;
  enableHoverCard?: boolean;
}

const TimelineItemComponent: React.FC<TimelineItemComponentProps> = ({
  item,
  hoverContent,
  enableHoverCard = false,
  ...props
}) => {
  return (
    <div className="flex gap-2 group relative mt-[-4px] before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[1px] before:bg-gray-400 before:ml-[5px] before:mt-1 last:before:hidden z-20">
      {/* Timeline marker and line */}
      <div className="flex flex-col items-center flex-shrink-0 mt-[4px]">
        <div
          className={cn(
            "w-3 h-3 rounded-full border flex-shrink-0 z-10",
            item.current ? "border-gray-700 bg-gray-700" : "border-gray-400 bg-white"
          )}
        />
      </div>

      {/* Content */}
      <div className="flex-1 pb-[14px] min-w-0">
        <div className="text-sm text-[var(--text--tertiary,rgba(42,42,42,0.56))] font-['Poppins',sans-serif] leading-5 rounded-md px-2 py-1 my-[-4px] ml-[-4px] max-w-full">
          {enableHoverCard ? (
            <HoverCard>
              <HoverCardTrigger className="hover:cursor-pointer max-w-full inline-block">
                {item.description || item.descriptionContent}
              </HoverCardTrigger>
              <HoverCardContent
                className="p-1 mr-8 max-w-xs break-all word-break-break-all overflow-wrap-break-word"
                {...props}
              >
                {hoverContent}
              </HoverCardContent>
            </HoverCard>
          ) : (
            <span className="max-w-full inline-block">
              {item.description || <p style={{ fontStyle: "italic" }}>no prompt</p>}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export { Timeline };
